import React, { useState, useCallback, useEffect, useRef } from 'react';
import { IconButton, CircularProgress, Tooltip, Box, Alert } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { useWhisper } from '../../hooks/useWhisper';
import { CustomSelect } from '../../components/CustomFields/CustomSelect';
import './SpeechMic.scss';

const log = (message: string, data?: any) => {
  const timestamp = new Date().toLocaleTimeString();
  console.log(`[${timestamp}] 🎤 SPEECHMIC: ${message}`, data || '');
};

const logError = (message: string, error?: any) => {
  const timestamp = new Date().toLocaleTimeString();
  console.error(`[${timestamp}] ❌ SPEECHMIC ERROR: ${message}`, error || '');
};

const logSuccess = (message: string, data?: any) => {
  const timestamp = new Date().toLocaleTimeString();
  console.log(`[${timestamp}] ✅ SPEECHMIC SUCCESS: ${message}`, data || '');
};

interface SpeechMicProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const LANGUAGES = [
  { value: "en-US", label: "English" },
  { value: "hi-IN", label: "हिंदी (Hindi)" },
  { value: "gu-IN", label: "ગુજરાતી (Gujarati)" },
  { value: "mr-IN", label: "मराठी (Marathi)" },
  { value: "bn-IN", label: "বাংলা (Bengali)" },
  { value: "pa-IN", label: "ਪੰਜਾਬੀ (Punjabi)" },
  { value: "ta-IN", label: "தமிழ் (Tamil)" },
  { value: "te-IN", label: "తెలుగు (Telugu)" },
  { value: "kn-IN", label: "ಕನ್ನಡ (Kannada)" },
  { value: "ml-IN", label: "മലയാളം (Malayalam)" },
  { value: "ur-IN", label: "اردو (Urdu)" },

  { value: "es-ES", label: "Español (Spanish)" },
  { value: "fr-FR", label: "Français (French)" },
  { value: "de-DE", label: "Deutsch (German)" },
  { value: "it-IT", label: "Italiano (Italian)" },
  { value: "pt-PT", label: "Português (Portuguese)" },
  { value: "ru-RU", label: "Русский (Russian)" },
  { value: "zh-CN", label: "中文 (Chinese)" },
  { value: "ja-JP", label: "日本語 (Japanese)" },
  { value: "ko-KR", label: "한국어 (Korean)" },
  { value: "ar-SA", label: "العربية (Arabic)" }
];

export const SpeechMic: React.FC<SpeechMicProps> = ({
  onTranscript,
  disabled = false,
  size = 'medium',
}) => {
  log('SpeechMic component mounted', { size, disabled });

  const { startLiveTranscription, stopLiveTranscription, isLoading, error: modelError } = useWhisper();
  const [isRecording, setIsRecording] = useState(false);
  const [showError, setShowError] = useState<string | null>(null);
  const [liveText, setLiveText] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState({ label: 'English', value: 'en-US' });

  const handleTranscript = useCallback(
    (text: string, isFinal: boolean) => {
      log('Transcript received', { text, isFinal, length: text.length });

      if (isFinal) {
        // Final result - send to parent and clear live text
        const finalSentence = text.trim();
        if (finalSentence) {
          logSuccess('Final sentence', { text: finalSentence });
          
          // Clear live text
          setLiveText('');
          
          // Send final sentence to parent (will be appended in editor)
          onTranscript(finalSentence);
        }
      } else {
        // Interim result - show only current interim text in live display
        setLiveText(text);
      }
    },
    [onTranscript]
  );

  const handleStart = useCallback(async () => {
    log('handleStart called', { language: selectedLanguage.value });
    try {
      setShowError(null);
      setLiveText('');
      setIsRecording(true);

      log('Starting live transcription');
      startLiveTranscription(handleTranscript, selectedLanguage.value);
      logSuccess('Live transcription started');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to start recording';
      logError('handleStart error', errorMsg);
      setShowError(errorMsg);
      setIsRecording(false);
    }
  }, [startLiveTranscription, handleTranscript, selectedLanguage.value]);

  const handleStop = useCallback(async () => {
    log('handleStop called');
    try {
      setShowError(null);
      setIsRecording(false);

      log('Stopping live transcription');
      stopLiveTranscription();
      setLiveText('');
      logSuccess('Live transcription stopped');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to stop recording';
      logError('handleStop error', errorMsg);
      setShowError(errorMsg);
    }
  }, [stopLiveTranscription]);

  const handleLanguageChange = (selectedOption: any) => {
    const newLanguage = selectedOption;
    log('Language changed', { from: selectedLanguage.value, to: newLanguage.value });
    setSelectedLanguage(newLanguage);

    if (isRecording) {
      log('Restarting transcription with new language');
      stopLiveTranscription();
      setTimeout(() => {
        startLiveTranscription(handleTranscript, newLanguage.value);
      }, 100);
    }
  };

  const isDisabled = disabled || isLoading;

  const getTooltip = () => {
    if (isLoading) return 'Loading speech recognition...';
    if (modelError) return 'Speech recognition unavailable';
    if (isRecording) return 'Click to stop recording';
    return 'Click to start recording';
  };

  log('Rendering SpeechMic', {
    isDisabled,
    isRecording,
    isLoading,
    hasError: !!showError,
    liveTextLength: liveText.length,
    selectedLanguage: selectedLanguage.value
  });

  return (
    <Box className="speech-mic-container">
      {(showError || modelError) && (
        <Alert severity="error" sx={{ mb: 1, fontSize: '12px' }}>
          {showError || modelError}
        </Alert>
      )}

      {liveText && (
        <Box className="speech-mic-text-display">
          <Box className="speech-mic-live-text">
            {liveText}
          </Box>
        </Box>
      )}

      <Box className="speech-mic-controls">
      {React.createElement(CustomSelect as any, {
        id: 'language-select',
        FieldName: 'language',
        options: LANGUAGES,
        value: selectedLanguage,
        isNotCreatable: true,
        handleChange: handleLanguageChange,
        isDisabled: isRecording || isDisabled,
        isClearable: false,
        isSearchable: false,
        className: 'speech-mic-language-select',
        styles: {
          control: (base: any) => ({
            ...base,
            minHeight: '40px',
            fontSize: '13px',
          }),
        },
        placeholder: 'Select Language',
      })}

        <Tooltip title={getTooltip()}>
          <span>
            <IconButton
              size={size}
              onClick={isRecording ? handleStop : handleStart}
              disabled={isDisabled}
              className={`speech-mic-button ${isRecording ? 'active' : ''}`}
              aria-label="speech-to-text"
            >
              {isLoading ? (
                <CircularProgress size={size === 'small' ? 20 : size === 'large' ? 32 : 24} />
              ) : isRecording ? (
                <MicOffIcon className="mic-icon recording" />
              ) : (
                <MicIcon className="mic-icon" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default SpeechMic;
